import { useState } from 'react';
import { Camera, ImagePlus } from 'lucide-react';
import { capturePhoto, type CapturedPhoto } from '@/lib/photoCapture';
import { useMediaUrl, type MediaView } from '@/lib/partnerQuotes';
import styles from '@/pages/partner-quotes/Quotes.module.css';

function Thumb({ photo, onRemove }: { photo: MediaView; onRemove?: () => void }) {
  const url = useMediaUrl(photo.id);
  const when = photo.taken_at ? new Date(photo.taken_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  return <figure className={styles.photo} style={{ margin: 0 }}>
    {url ? <img src={url} alt={photo.caption || `Photo taken ${when}`} loading="lazy" /> : null}
    <figcaption>{[photo.caption, when, photo.location ? 'Located' : null].filter(Boolean).join(' · ')}</figcaption>
    {onRemove && <button type="button" className={styles.photoRemove} aria-label="Remove photo" onClick={onRemove}>×</button>}
  </figure>;
}

/**
 * As many photos as the job needs (Warren, 21 October 2026): take one with the camera or add several from the
 * library. Each is compressed on the phone and carries when and where it was taken. `upload` decides where it goes:
 * straight to the Api, or into the offline queue on install day.
 */
export function PhotoGrid({ photos, upload, onRemove, label = 'Photos', pending = [] }: {
  photos: MediaView[]; label?: string; pending?: Array<{ client_id: string; caption: string }>;
  upload?: (photo: CapturedPhoto) => Promise<unknown>; onRemove?: (id: string) => void;
}) {
  const [busy, setBusy] = useState(0), [message, setMessage] = useState('');
  async function add(files: FileList | null) {
    if (!files?.length || !upload) return;
    setMessage(''); setBusy(n => n + files.length);
    for (const file of Array.from(files)) {
      try { await upload(await capturePhoto(file)); } catch (err) { setMessage(err instanceof Error ? err.message : 'A photo did not upload.'); } finally { setBusy(n => n - 1); }
    }
  }
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div className={styles.photos} aria-label={label}>
      {photos.map(p => <Thumb key={p.id} photo={p} onRemove={onRemove ? () => onRemove(p.id) : undefined} />)}
      {pending.map(p => <div key={p.client_id} className={styles.photo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, padding: 6, textAlign: 'center' }}>
        {p.caption || 'Saved on this phone. Uploads when you are back online.'}</div>)}
      {upload && <>
        <label className={styles.addPhoto}><Camera size={22} aria-hidden />Take photo
          <input type="file" accept="image/*" capture="environment" onChange={e => { void add(e.target.files); e.target.value = ''; }} /></label>
        <label className={styles.addPhoto}><ImagePlus size={22} aria-hidden />Add photos
          <input type="file" accept="image/*" multiple onChange={e => { void add(e.target.files); e.target.value = ''; }} /></label>
      </>}
    </div>
    {busy > 0 && <p className={styles.status} role="status">Preparing {busy} photo{busy === 1 ? '' : 's'}…</p>}
    {message && <p className={styles.status} role="alert">{message}</p>}
  </div>;
}
